/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import React, { useState } from 'react';
import { ModalMenu } from '../../../ui-library/DropdownMenu/ModalMenu';

type CancelDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  shopName: string;
  courseName: string;
  date: string;
  time: string;
  showCancelReason?: boolean;
};

export const CancelDialog: React.FC<CancelDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  shopName,
  courseName,
  date,
  time,
  showCancelReason = false,
}) => {
  const [cancelReason, setCancelReason] = useState('');
  const styles = {
    hiddenTrigger: css`
      display: none;
    `,
    descriptionContainer: css`
      width: 100%;
    `,
    warningText: css`
      font-size: 14px;
      color: #666;
      margin: 0 0 16px 0;
      text-align: left;
    `,
    reservationInfo: css`
      background-color: #f5f5f5;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;
      text-align: left;
    `,
    textFieldContainer: css`
      margin-bottom: 24px;
    `,
    textFieldLabel: css`
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
      display: block;
    `,
    required: css`
      color: white;
      margin-left: 8px;
      font-size: 10px;
      font-weight: 600;
      background-color: #e63a1c;
      padding: 2px 6px;
      border-radius: 25px;
    `,
    textField: css`
      width: 100%;
      padding: 12px 16px;
      font-size: 14px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      resize: none;
      font-family: inherit;
      line-height: 1.5;

      &:focus {
        outline: none;
        border-color: #666;
      }

      &::placeholder {
        color: #999;
      }
    `,
    shopName: css`
      font-size: 14px;
      color: #666;
      margin-bottom: 4px;
    `,
    courseName: css`
      font-size: 16px;
      font-weight: bold;
      color: #333;
      margin-bottom: 8px;
    `,
    dateTime: css`
      font-size: 14px;
      color: #666;
    `,
    buttons: css`
      display: flex;
      gap: 12px;
      justify-content: center;
    `,
    closeButton: css`
      flex: 1;
      padding: 12px 24px;
      border: 1px solid #ddd;
      background-color: white;
      color: #333;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      &:hover {
        background-color: #f5f5f5;
      }
    `,
    cancelButton: css`
      flex: 1;
      padding: 12px 24px;
      border: none;
      background-color: #e63a1c;
      color: white;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      &:hover:not(:disabled) {
        background-color: #d32f0f;
      }
      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
      }
    `,
  };

  return (
    <>
      <div css={styles.hiddenTrigger} />
      <ModalMenu
        isMenuOpen={isOpen}
        setIsMenuOpen={(open) => {
          if (!open) {
            onClose();
            setCancelReason('');
          }
        }}
        modalTitle="予約を取り消しますか？"
        size="large"
        description={
          <div css={styles.descriptionContainer}>
            <p css={styles.warningText}>予約を取り消すと元に戻せません</p>
            <div css={styles.reservationInfo}>
              <div css={styles.shopName}>{shopName}</div>
              <div css={styles.courseName}>{courseName}</div>
              <div css={styles.dateTime}>
                予約日： {date} {time}
              </div>
            </div>
            {showCancelReason && (
              <div css={styles.textFieldContainer}>
                <label css={styles.textFieldLabel}>
                  キャンセル理由
                  <span css={styles.required}>必須</span>
                </label>
                <textarea
                  css={styles.textField}
                  rows={3}
                  placeholder="キャンセル理由を入力してください"
                  value={cancelReason}
                  onChange={(e) => {
                    setCancelReason(e.target.value);
                  }}
                />
              </div>
            )}
            <div css={styles.buttons}>
              <button css={styles.closeButton} onClick={onClose} type="button">
                閉じる
              </button>
              <button
                css={styles.cancelButton}
                onClick={() => {
                  onConfirm(cancelReason);
                  setCancelReason('');
                }}
                type="button"
                disabled={showCancelReason && !cancelReason.trim()}
              >
                取り消す
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};
